import { textAlign } from '@mui/system';
import React, { PropsWithChildren, Suspense, useEffect, useRef, useState } from 'react';
import LoadingIndicator from '../LoadingIndicator';
import type { ISearchResultCarouselProps } from './SearchResultCarousel';
// import SearchResultCarousel from './SearchResultCarousel';
import VisibilitySensor from '../VisibilitySensor';

const SearchResultCarousel = React.lazy(() => import(/* webpackChunkName: "sres" */ './SearchResultCarousel'));

/**
 * full height of all result list items for placeholder 
 */
const getResultListHeight = (elm: HTMLDivElement, columns: number) => {

    if (!elm) return null;

    //always one column
    const listHeight = (312 + 20) / columns - 20;
    return listHeight;
}

/**
 * renders an placeholder div with correct height and lazy loads SearchResultCarousel
 */
const SearchResultCarouselPlaceholder: React.FC<React.PropsWithChildren<ISearchResultCarouselProps>> = ({ searchUrl = null, limit = 100, columns = 1, ...props }) => {
    return (
        <RenderFixedHeightDiv limit={limit} columns={columns}>
            <VisibilitySensor fallback={<RenderFallback />}>
                <RenderSearchResultCarousel searchUrl={searchUrl} limit={limit} columns={columns} {...props} />
            </VisibilitySensor>
        </RenderFixedHeightDiv>
    );
};

const RenderSearchResultCarousel: React.FC<React.PropsWithChildren<ISearchResultCarouselProps>> = ({ searchUrl, limit, columns, ...props }) => {
    return (
        <Suspense fallback={<div style={{ textAlign: 'center', width: '100%' }}><LoadingIndicator /></div >}>
            <SearchResultCarousel searchUrl={searchUrl} limit={limit} columns={columns} {...props} />
        </Suspense >
    )
}

const RenderFallback: React.FC<React.PropsWithChildren<{}>> = () => {
    return (
        <div>
            Immobilien auf <a href="https://www.wohnglueck.de">wohnglueck.de</a>. Bei uns findest du relevante Informationen und digitale Services rund um die Themen Immobiliensuche, Immobilienverkauf und -vermietung, Kaufen und Bauen sowie Modernisieren.
            <div style={{ textAlign: 'center', marginTop: '100px' }}><LoadingIndicator /></div>
        </div>
    )
}

const RenderFixedHeightDiv: React.FC<React.PropsWithChildren<PropsWithChildren<{ limit: number, columns: number }>>> = ({ children, limit = 6, columns = 1 }) => {
    const elm = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const currResizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                const newHeight = `${Math.ceil(getResultListHeight(elm.current, columns))}px`
                if ((entry.target as HTMLElement).style.minHeight != newHeight) {
                    (entry.target as HTMLElement).style.minHeight = `${Math.ceil(getResultListHeight(elm.current, columns))}px`;
                }
            }
        });

        if (elm.current) {
            elm.current.style.minHeight = `${Math.ceil(getResultListHeight(elm.current, columns))}px`;
            currResizeObserver.observe(elm.current);
        }

        return () => {
            currResizeObserver.unobserve(elm.current);
        }
    }, [elm])

    return <div ref={elm} style={{ display: "flex", alignItems: "stretch" }}>{children}</div>
}

export default SearchResultCarouselPlaceholder;
